import { Component } from '@angular/core';
import { NavParams, ToastController, ViewController } from 'ionic-angular';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { AuthService } from '../../../../_foundation/services/auth/auth.service';
import { User } from '../../../../_foundation/models/security.models';
import {
  ServiceDeskTicketRequest,
  SupportContext
} from '../../../../models/support/support.models';
import { ServiceDeskTicketService } from '../../../../services/support/service-desk-ticket.service';

@Component({
  selector: 'service-desk-ticket-modal',
  templateUrl: 'service-desk-ticket-modal.component.html'
})
export class ServiceDeskTicketModalComponent {
  private static readonly allowedAttachmentExtensions = [
    'bmp',
    'apng',
    'avif',
    'csv',
    'doc',
    'docx',
    'gif',
    'heic',
    'heif',
    'ico',
    'jfif',
    'jpe',
    'jpeg',
    'jpg',
    'odp',
    'ods',
    'odt',
    'pdf',
    'png',
    'ppt',
    'pptx',
    'rtf',
    'svg',
    'tif',
    'tiff',
    'txt',
    'webp',
    'xls',
    'xlsx',
    'zip'
  ];
  private static readonly allowedAttachmentMimeTypes = [
    'application/msword',
    'application/pdf',
    'application/rtf',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/x-zip-compressed',
    'application/zip',
    'text/csv',
    'text/plain'
  ];
  readonly acceptedAttachmentTypes = [
    'image/*',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.odt',
    '.ods',
    '.odp',
    '.txt',
    '.rtf',
    '.csv',
    '.zip'
  ].join(',');
  model: ServiceDeskTicketRequest = new ServiceDeskTicketRequest();
  attachmentPreviewUrls: string[] = [];
  context: SupportContext;
  submitting: boolean;
  submitted: boolean;
  formSubmitted: boolean;

  constructor(
    private navParams: NavParams,
    private viewController: ViewController,
    private toastController: ToastController,
    private authService: AuthService,
    private serviceDeskTicketService: ServiceDeskTicketService
  ) {
    this.context = this.navParams.get('context') || new SupportContext();
    this.model.contextLabel = this.context.label;
    this.model.contextKey = this.context.key;
    this.model.contextDetails = this.context.details;
    this.model.reviewedTrainingVideos = false;
    this.setCurrentUserDetails();
  }

  closeModal() {
    this.viewController.dismiss();
  }

  fileDropped(event: NgxFileDropEntry[]) {
    let pendingFileCount = 0;
    let rejectedFileCount = 0;

    for (const dropped of event) {
      if (!dropped.fileEntry.isFile) {
        continue;
      }

      pendingFileCount++;
      const fileEntry = dropped.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        if (!this.isAllowedAttachment(file)) {
          rejectedFileCount++;
        } else {
          this.addAttachment(file);
        }

        pendingFileCount--;
        if (pendingFileCount === 0 && rejectedFileCount > 0) {
          this.presentAttachmentRejectedToast(rejectedFileCount);
        }
      });
    }
  }

  removeAttachment(index: number) {
    this.model.attachments.splice(index, 1);
    this.attachmentPreviewUrls.splice(index, 1);
  }

  downloadAttachment(index: number) {
    const attachment = this.model.attachments[index];

    if (!attachment) {
      return;
    }

    const url = URL.createObjectURL(attachment);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.name;
    link.click();

    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  onIssueDescriptionInput(element: HTMLElement) {
    this.model.issueDescription = element.innerHTML;
  }

  onIssueDescriptionPaste(event: ClipboardEvent, editor: HTMLElement) {
    if (!event.clipboardData || !event.clipboardData.files || event.clipboardData.files.length === 0) {
      setTimeout(() => this.syncIssueDescriptionFromEditor(editor));
      return;
    }

    const pastedImages = Array.from(event.clipboardData.files)
      .filter(file => file.type.indexOf('image/') === 0);

    if (pastedImages.length === 0) {
      setTimeout(() => this.syncIssueDescriptionFromEditor(editor));
      return;
    }

    event.preventDefault();

    pastedImages.forEach(file => {
      const attachment = this.renamePastedFile(file);
      this.addAttachment(attachment);
      this.insertHtmlAtCursor(`<p><strong>Screenshot attached:</strong> ${this.escapeHtml(attachment.name)}</p>`);
    });

    this.syncIssueDescriptionFromEditor(editor);
  }

  isTicketValid(): boolean {
    return this.hasValue(this.model.name)
      && this.isEmailValid(this.model.email)
      && this.hasValue(this.getIssueDescriptionText());
  }

  getSubmitDisabledReason(): string {
    const missingFields: string[] = [];

    if (!this.hasValue(this.model.name)) {
      missingFields.push('Name');
    }

    if (!this.hasValue(this.model.email)) {
      missingFields.push('Your Email');
    }

    if (!this.hasValue(this.getIssueDescriptionText())) {
      missingFields.push('Brief description of issue');
    }

    const messages: string[] = [];

    if (missingFields.length > 0) {
      messages.push(`Required: ${missingFields.join(', ')}.`);
    }

    if (this.hasValue(this.model.email) && !this.isEmailValid(this.model.email)) {
      messages.push('Enter an email in the format x@y.');
    }

    return messages.join(' ');
  }

  shouldShowRequiredError(value: string): boolean {
    return this.formSubmitted && !this.hasValue(value);
  }

  shouldShowDescriptionRequiredError(): boolean {
    return this.formSubmitted && !this.hasValue(this.getIssueDescriptionText());
  }

  submitTicket() {
    this.formSubmitted = true;

    if (!this.isTicketValid()) {
      this.presentToast('Name, email and brief description are required');
      return;
    }

    this.submitting = true;

    this.serviceDeskTicketService.submitTicket(this.model).subscribe({
      next: () => {
        this.submitting = false;
        this.submitted = true;
      },
      error: () => {
        this.submitting = false;
        this.presentToast('Ticket request could not be submitted');
      }
    });
  }

  private setCurrentUserDetails() {
    this.model.email = '';

    try {
      const user = this.authService.currentUser<User>();

      if (!user) {
        return;
      }

      const firstName = (user as any).firstName || '';
      const lastName = (user as any).lastName || '';
      this.model.name = user.name || `${firstName} ${lastName}`.trim() || user.username || '';
      this.model.email = user.emailAddress || '';
    } catch {
      this.model.name = '';
      this.model.email = '';
    }
  }

  private addAttachment(file: File) {
    this.model.attachments.push(file);
    this.attachmentPreviewUrls.push('');

    if (!file || !file.type || file.type.indexOf('image/') !== 0) {
      return;
    }

    const previewIndex = this.attachmentPreviewUrls.length - 1;
    const reader = new FileReader();
    reader.onload = () => {
      this.attachmentPreviewUrls[previewIndex] = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  private isAllowedAttachment(file: File): boolean {
    if (!file) {
      return false;
    }

    if (file.type && file.type.indexOf('image/') === 0) {
      return true;
    }

    if (
      file.type &&
      ServiceDeskTicketModalComponent.allowedAttachmentMimeTypes.indexOf(file.type.toLowerCase()) !== -1
    ) {
      return true;
    }

    const extension = this.getFileNameExtension(file.name);

    return ServiceDeskTicketModalComponent.allowedAttachmentExtensions.indexOf(extension) !== -1;
  }

  private getFileNameExtension(fileName: string): string {
    if (!fileName || fileName.indexOf('.') === -1) {
      return '';
    }

    return fileName.split('.').pop().toLowerCase();
  }

  private syncIssueDescriptionFromEditor(element: HTMLElement) {
    this.model.issueDescription = element ? element.innerHTML : '';
  }

  private getIssueDescriptionText(): string {
    const container = document.createElement('div');
    container.innerHTML = this.model.issueDescription || '';

    return (container.textContent || '').trim();
  }

  private hasValue(value: string): boolean {
    return !!value && value.trim().length > 0;
  }

  private isEmailValid(value: string): boolean {
    const email = (value || '').trim();
    const atIndex = email.indexOf('@');

    return atIndex > 0
      && atIndex === email.lastIndexOf('@')
      && atIndex < email.length - 1;
  }

  private renamePastedFile(file: File): File {
    const extension = this.getFileExtension(file.type);
    const baseName = `pasted-image-${this.formatFileTimestamp(new Date())}`;
    const name = this.getUniqueAttachmentName(baseName, extension);

    try {
      return new File([file], name, {type: file.type});
    } catch {
      return file;
    }
  }

  private getUniqueAttachmentName(baseName: string, extension: string): string {
    const existingNames = (this.model.attachments || [])
      .map(attachment => attachment.name.toLowerCase());
    let candidate = `${baseName}${extension}`;
    let suffix = 2;

    while (existingNames.indexOf(candidate.toLowerCase()) !== -1) {
      candidate = `${baseName}-${suffix}${extension}`;
      suffix++;
    }

    return candidate;
  }

  private formatFileTimestamp(date: Date): string {
    return [
      date.getFullYear(),
      this.padDatePart(date.getMonth() + 1),
      this.padDatePart(date.getDate())
    ].join('-') + '-' + [
      this.padDatePart(date.getHours()),
      this.padDatePart(date.getMinutes()),
      this.padDatePart(date.getSeconds())
    ].join('');
  }

  private padDatePart(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  private getFileExtension(contentType: string): string {
    switch (contentType) {
      case 'image/jpeg':
        return '.jpg';
      case 'image/gif':
        return '.gif';
      case 'image/webp':
        return '.webp';
      case 'image/png':
      default:
        return '.png';
    }
  }

  private insertHtmlAtCursor(html: string) {
    document.execCommand('insertHTML', false, html);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private presentToast(message: string) {
    const toast = this.toastController.create({
      message,
      duration: 10000,
      position: 'bottom'
    });

    toast.present();
  }

  private presentAttachmentRejectedToast(rejectedFileCount: number) {
    const supportedTypes = 'images, PDFs, documents, spreadsheets, presentations, text files, CSVs or ZIP files.';
    const message = rejectedFileCount === 1
      ? `Attachment type not supported. Add ${supportedTypes}`
      : `${rejectedFileCount} attachment types not supported. Add ${supportedTypes}`;

    this.presentToast(message);
  }
}



// WEBPACK FOOTER //
// ./src/pages/support/components/service-desk-ticket-modal/service-desk-ticket-modal.component.ts